import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, Truck, Tag, FileText, LogOut, MessageSquare, Mail, Bell, Users, Sparkles, BarChart3, FlaskConical } from "lucide-react";
import { BusinessTab } from "../components/admin/biz/BusinessTab";
import { LabTab } from "../components/admin/lab/LabTab";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { AdminAssistant } from "../components/AdminAssistant";
import api from "../lib/api";
import { toast } from "sonner";

import { ProductForm } from "../components/admin/ProductForm";
import { ChatAdmin } from "../components/admin/ChatAdmin";
import { DashboardTab } from "../components/admin/tabs/DashboardTab";
import { ProductsTab } from "../components/admin/tabs/ProductsTab";
import { OrdersTab } from "../components/admin/tabs/OrdersTab";
import { CustomersTab } from "../components/admin/tabs/CustomersTab";
import { ShippingTab } from "../components/admin/tabs/ShippingTab";
import { DiscountsTab } from "../components/admin/tabs/DiscountsTab";
import { ContentTab } from "../components/admin/tabs/ContentTab";
import { MessagesTab } from "../components/admin/tabs/MessagesTab";
import { NewsletterTab } from "../components/admin/tabs/NewsletterTab";

export default function Admin() {
  const { user, loading, isAdmin, logout } = useAuth();
  const { t: tt, lang } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState({ shipping_fees: {}, free_shipping_threshold: 0, instagram_url: "", email: "" });
  const [discounts, setDiscounts] = useState([]);
  const [content, setContent] = useState({});
  const [editing, setEditing] = useState(null);
  const [newDisc, setNewDisc] = useState({ code: "", type: "percent", value: 10 });
  const [logoUploading, setLogoUploading] = useState(false);
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroUrl, setHeroUrl] = useState("");
  const [messages, setMessages] = useState([]);
  const [subs, setSubs] = useState([]);
  const [archives, setArchives] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [resetting, setResetting] = useState(false);

  const uploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...settings, logo_url: data.url };
      setSettings(next);
      await api.put("/settings", next);
      toast.success("Logo updated");
    } catch {
      toast.error("Upload failed");
    } finally {
      setLogoUploading(false);
    }
  };

  const uploadHero = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setHeroUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const list = [...(settings.hero_images || []), data.url];
      const next = { ...settings, hero_images: list, hero_image: list[0] };
      setSettings(next);
      await api.put("/settings", next);
      toast.success("Hero image added");
    } catch {
      toast.error("Upload failed");
    } finally {
      setHeroUploading(false);
    }
  };

  const removeHero = async (idx) => {
    const list = (settings.hero_images || []).filter((_, i) => i !== idx);
    const next = { ...settings, hero_images: list, hero_image: list[0] || "" };
    setSettings(next);
    await api.put("/settings", next);
    toast.success("Removed");
  };

  useEffect(() => { if (!loading && !isAdmin) navigate("/login"); }, [loading, isAdmin, navigate]);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/discounts").then((r) => setDiscounts(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/messages").then((r) => setMessages(r.data)).catch(() => {});
    api.get("/newsletter").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/admin/stats/archives").then((r) => setArchives(r.data)).catch(() => {});
    api.get("/admin/customers").then((r) => setCustomers(r.data.customers || [])).catch(() => {});
  }, []);
  useEffect(() => { if (isAdmin) loadAll(); }, [isAdmin, loadAll]);

  if (!isAdmin) return <div className="py-40 text-center text-[#8A857D]">…</div>;

  const saveProduct = async (p) => {
    try {
      if (p.id) await api.put(`/products/${p.id}`, { ...p, stock_before: products.find((x) => x.id === p.id)?.stock });
      else await api.post("/products", p);
      toast.success("Saved");
      setEditing(null);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    loadAll();
  };
  const updateOrderStatus = async (id, status) => {
    const o = orders.find((x) => x.id === id);
    let restock = false;
    if (["cancelled", "returned", "refunded"].includes(status) && o?.payment_status === "paid" && !["cancelled", "returned", "refunded"].includes(o?.status)) {
      restock = window.confirm(lang === "ar" ? "هل ترجع الزجاجات إلى المخزون؟ (موافق = نعم، إلغاء = لا، الزجاجة تالفة أو لم ترجع)" : "Return the bottles to sellable stock? (OK = yes, Cancel = no, bottle not returned)");
    }
    try {
      await api.put(`/orders/${id}/status`, { status, restock });
      toast.success("Order updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update order");
    }
    loadAll();
  };
  const saveSettings = async () => {
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings");
    }
  };
  const saveContent = async () => {
    await api.put("/content", content);
    toast.success("Content saved");
  };
  const addDiscount = async () => {
    if (!newDisc.code) return;
    await api.post("/discounts", newDisc);
    toast.success("Discount added");
    setNewDisc({ code: "", type: "percent", value: 10 });
    api.get("/discounts").then((r) => setDiscounts(r.data));
  };
  const delDiscount = async (code) => {
    await api.delete(`/discounts/${code}`);
    api.get("/discounts").then((r) => setDiscounts(r.data));
  };

  const resetStats = async () => {
    if (!window.confirm("سيتم حفظ الأرقام الحالية في الأرشيف ثم تصفير العدّادات. الطلبات الحقيقية لن تُحذف. متابعة؟")) return;
    setResetting(true);
    try {
      await api.post("/admin/stats/reset", {});
      toast.success("تم حفظ اللقطة وتصفير العدّادات");
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
      api.get("/admin/stats/archives").then((r) => setArchives(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل التصفير");
    } finally {
      setResetting(false);
    }
  };

  const deleteArchive = async (id) => {
    if (!window.confirm("حذف هذه اللقطة من الأرشيف نهائياً؟")) return;
    await api.delete(`/admin/stats/archives/${id}`);
    toast.success("تم الحذف");
    api.get("/admin/stats/archives").then((r) => setArchives(r.data)).catch(() => {});
  };

  const downloadArchiveCsv = async (id) => {
    try {
      const res = await api.get(`/admin/stats/archives/${id}/csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `omr-stats-${id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("تعذّر تنزيل الملف");
    }
  };

  const deleteMember = async (id) => {
    if (!window.confirm("حذف هذا العضو؟ سيُزال من القائمة ولن يتمكّن من تسجيل الدخول.")) return;
    await api.delete(`/admin/customers/${id}`);
    toast.success("تم حذف العضو (يمكن استرجاعه)");
    api.get("/admin/customers").then((r) => setCustomers(r.data.customers || [])).catch(() => {});
  };

  const restoreMember = async (id) => {
    await api.post(`/admin/customers/${id}/restore`);
    toast.success("تم استرجاع العضو");
    api.get("/admin/customers").then((r) => setCustomers(r.data.customers || [])).catch(() => {});
  };

  const tabs = [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["business", lang === "ar" ? "إدارة الأعمال" : "Business", BarChart3],
    ["lab", lang === "ar" ? "مختبر التجارب" : "Experimental Lab", FlaskConical],
    ["products", "Products", Package],
    ["orders", "Orders", ShoppingCart],
    ["customers", "Members", Users],
    ["shipping", "Shipping", Truck],
    ["discounts", "Discounts", Tag],
    ["content", "Content", FileText],
    ["chat", "Live Chat", MessageSquare],
    ["messages", "Enquiries", Mail],
    ["newsletter", "Newsletter", Bell],
    ["assistant", tt("assistant_tab"), Sparkles],
  ];

  const ctx = { tab, setTab, stats, setStats, products, setProducts, orders, setOrders, settings, setSettings, discounts, setDiscounts, content, setContent, editing, setEditing, newDisc, setNewDisc, logoUploading, setLogoUploading, heroUploading, setHeroUploading, heroUrl, setHeroUrl, messages, setMessages, subs, setSubs, archives, setArchives, customers, setCustomers, memberSearch, setMemberSearch, resetting, setResetting, navigate, uploadLogo, uploadHero, removeHero, loadAll, saveProduct, deleteProduct, updateOrderStatus, saveSettings, saveContent, addDiscount, delDiscount, resetStats, deleteArchive, downloadArchiveCsv, deleteMember, restoreMember, user, loading, isAdmin, logout, tt };

  return (
    <div className="admin-root min-h-screen bg-[#0B0A08] flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-60 border-b md:border-b-0 md:border-e border-[#262421] p-4 md:p-6 flex md:flex-col gap-1 md:gap-2 overflow-x-auto">
        <div className="hidden md:block serif text-xl tracking-[0.2em] mb-8">OMR ADMIN</div>
        {tabs.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`} className={`flex items-center gap-3 px-4 py-2.5 text-xs uppercase tracking-[0.12em] whitespace-nowrap transition-colors ${tab === id ? "bg-[#C49A6C] text-[#0B0A08]" : "text-[#8A857D] hover:text-[#F2EBE1]"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
        <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="flex items-center gap-3 px-4 py-3 mt-2 md:mt-6 md:border-t border-[#262421] text-xs uppercase tracking-[0.12em] text-[#C49A6C] hover:text-[#F2EBE1] hover:bg-[#1A1A1A] transition-colors"><LogOut size={16} /> Logout</button>
      </aside>

      {/* Main */}
      <main className="flex-1 p-5 md:p-10 overflow-x-hidden">
        {tab === "dashboard" && <DashboardTab ctx={ctx} />}

        {tab === "business" && <BusinessTab />}

        {tab === "lab" && <LabTab />}

        {tab === "products" && <ProductsTab ctx={ctx} />}

        {tab === "orders" && <OrdersTab ctx={ctx} />}

        {tab === "customers" && <CustomersTab ctx={ctx} />}

        {tab === "shipping" && <ShippingTab ctx={ctx} />}

        {tab === "discounts" && <DiscountsTab ctx={ctx} />}

        {tab === "content" && <ContentTab ctx={ctx} />}

        {tab === "chat" && <ChatAdmin />}

        {tab === "messages" && <MessagesTab ctx={ctx} />}

        {tab === "newsletter" && <NewsletterTab ctx={ctx} />}

        {tab === "assistant" && <AdminAssistant />}
      </main>

      {editing && <ProductForm initial={editing} onSave={saveProduct} onCancel={() => setEditing(null)} />}
    </div>
  );
}
